import Vue from 'vue';
import VueRouter from 'vue-router';
import i18n from '@/lang';
import { cities } from '@/data/cities';
import { lockerLocations } from '@/data/lockerLocations';
import store from '@/stores/store';
import { setHeadAttributes } from '../scripts/seoHeadTools';

// Views
import Home from '@/views/Home.vue';
import Search from '@/views/SearchStores.vue';
import Bookings from '@/views/Bookings.vue';
import BookingDetails from '@/views/BookingDetails.vue';
import Account from '@/views/Account.vue';
import CityHome from '@/views/CityHome.vue';
import LockerHome from '@/views/LockerHome.vue';
import FullHelp from '@/views/HelpSection.vue';
import TermsAndConditions from '@/views/TermsAndConditions.vue';
import Transfers from '@/views/Transfers.vue';
import NotFound from '@/views/404.vue';

Vue.use(VueRouter);

function detectLang() {
  const supportedLangs = ['es', 'en', 'pt', 'it'];
  const stored = store.state.main.lang;
  const nav = navigator.language.slice(0, 2);
  const fallback = 'es';

  return supportedLangs.includes(stored) ? stored
    : supportedLangs.includes(nav) ? nav
      : fallback;
}

const lang = detectLang();

function formatDateForQuery(date) {
  return `${date.getFullYear()}/${
    (`00${date.getMonth() + 1}`).slice(-2)}/${
    (`00${date.getDate()}`).slice(-2)} ${
    (`00${date.getHours()}`).slice(-2)}:${
    (`00${date.getMinutes()}`).slice(-2)}:${
    (`00${date.getSeconds()}`).slice(-2)}`;
}

function getAeroCorSearchDates() {
  const startDate = new Date();
  const endDate = new Date(startDate);

  startDate.setHours(startDate.getHours(), 0, 0, 0);
  endDate.setHours(23, 0, 0, 0);

  return {
    startDate: formatDateForQuery(startDate),
    endDate: formatDateForQuery(endDate),
  };
}

const router = new VueRouter({

  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    }
    return { x: 0, y: 0, behavior: 'smooth' };
  },

  mode: 'history',
  routes: [
    // Url que no llevan idioma
    {
      path: '/process_payment/:payment_provider?',
      name: 'ProcessPayment',
      component: () => import('./views/ProcessPayment.vue'),
    },
    {
      path: '/auth/:provider/:action',
      name: 'ProcessSocialAuth',
      component: () => import('./views/ProcessSocialAuth.vue'),
    },
    {
      path: '/auth/store_app_data',
      name: 'StoreAppAuthData',
      component: () => import('./views/StoreAppAuthData.vue'),
    },
    {
      path: '/password/reset/:token?',
      name: 'ResetPassword',
      component: () => import('./views/ResetPassword.vue'),
    },

    // Url viejas
    {
      path: '/cities/:city_id',
      name: 'cities',
      redirect: (to) => `/es/guarderia-equipaje/${to.params.city_id}`,
    },

    // 🇪🇸 Español
    // no funciona más el :action? del home (register o login)
    // ver en caso que se necesite.
    { path: '/es/', name: 'HomeEs', component: Home },
    { path: '/es/bookings', name: 'BookingsEs', component: Bookings },
    { path: '/es/bookings/:id', name: 'BookingDetailsEs', component: BookingDetails },
    { path: '/es/account', name: 'AccountEs', component: Account },
    { path: '/es/search', name: 'SearchEs', component: Search },
    { path: '/es/search/:city_id?', name: 'SearchEsCity', component: Search },
    { path: '/es/guarderia-equipaje/:city_id', name: 'CityHomeEs', component: CityHome },
    { path: '/es/lockers/:location', name: 'LockerHomeEs', component: LockerHome },
    { path: '/es/transfer/:city_id', name: 'TransfersEs', component: Transfers },
    { path: '/es/full-help', name: 'FullHelpEs', component: FullHelp },
    { path: '/es/terms-and-conditions', name: 'TermsAndConditionsEs', component: TermsAndConditions },

    // 🇬🇧 Inglés
    { path: '/en/', name: 'HomeEn', component: Home },
    { path: '/en/bookings', name: 'BookingsEn', component: Bookings },
    { path: '/en/bookings/:id', name: 'BookingDetailsEn', component: BookingDetails },
    { path: '/en/account', name: 'AccountEn', component: Account },
    { path: '/en/search', name: 'SearchEn', component: Search },
    { path: '/en/search/:city_id?', name: 'SearchEnCity', component: Search },
    { path: '/en/luggage-storage/:city_id', name: 'CityHomeEn', component: CityHome },
    { path: '/en/lockers/:location', name: 'LockerHomeEn', component: LockerHome },
    { path: '/en/transfer/:city_id', name: 'TransfersEn', component: Transfers },
    { path: '/en/full-help', name: 'FullHelpEn', component: FullHelp },
    { path: '/en/terms-and-conditions', name: 'TermsAndConditionsEn', component: TermsAndConditions },

    // 🇵🇹 Portugués
    { path: '/pt/', name: 'HomePt', component: Home },
    { path: '/pt/bookings', name: 'BookingsPt', component: Bookings },
    { path: '/pt/bookings/:id', name: 'BookingDetailsPt', component: BookingDetails },
    { path: '/pt/account', name: 'AccountPt', component: Account },
    { path: '/pt/search', name: 'SearchPt', component: Search },
    { path: '/pt/search/:city_id?', name: 'SearchPtCity', component: Search },
    { path: '/pt/guarda-volumes/:city_id', name: 'CityHomePt', component: CityHome },
    { path: '/pt/lockers/:location', name: 'LockerHomePt', component: LockerHome },
    { path: '/pt/transfer/:city_id', name: 'TransfersPt', component: Transfers },
    { path: '/pt/full-help', name: 'FullHelpPt', component: FullHelp },
    { path: '/pt/terms-and-conditions', name: 'TermsAndConditionsPt', component: TermsAndConditions },

    // 🇮🇹 Italiano
    { path: '/it/', name: 'HomeIt', component: Home },
    { path: '/it/bookings', name: 'BookingsIt', component: Bookings },
    { path: '/it/bookings/:id', name: 'BookingDetailsIt', component: BookingDetails },
    { path: '/it/account', name: 'AccountIt', component: Account },
    { path: '/it/search', name: 'SearchIt', component: Search },
    { path: '/it/search/:city_id?', name: 'SearchItCity', component: Search },
    { path: '/it/deposito-bagagli/:city_id', name: 'CityHomeIt', component: CityHome },
    { path: '/it/lockers/:location', name: 'LockerHomeIt', component: LockerHome },
    { path: '/it/transfer/:city_id', name: 'TransfersIt', component: Transfers },
    { path: '/it/full-help', name: 'FullHelpIt', component: FullHelp },
    { path: '/it/terms-and-conditions', name: 'TermsAndConditionsIt', component: TermsAndConditions },

    // Url para agregar idiomas

    {
      path: '/',
      redirect: () => `/${lang}/`,
    },
    {
      path: '/bookings',
      name: 'BookingsRedirect',
      redirect: () => `/${lang}/bookings`,
    },
    {
      path: '/bookings/:id',
      name: 'BookingDetailsRedirect',
      redirect: (to) => `/${lang}/bookings/${to.params.id}`,
    },
    {
      path: '/account',
      name: 'AccountRedirect',
      redirect: () => `/${lang}/account`,
    },
    {
      path: '/search',
      name: 'SearchRedirect',
      redirect: () => `/${lang}/search`,
    },
    {
      path: '/search/:city_id?',
      name: 'SearchCityRedirect',
      redirect: (to) => `/${lang}/search/${to.params.city_id}`,
    },
    {
      path: '/guarderia-equipaje/:city_id',
      redirect: (to) => `/es/guarderia-equipaje/${to.params.city_id}`,
    },
    {
      path: '/luggage-storage/:city_id',
      redirect: (to) => `/en/luggage-storage/${to.params.city_id}`,
    },
    {
      path: '/guarda-volumes/:city_id',
      redirect: (to) => `/pt/guarda-volumes/${to.params.city_id}`,
    },
    {
      path: '/deposito-bagagli/:city_id',
      redirect: (to) => `/it/deposito-bagagli/${to.params.city_id}`,
    },
    {
      path: '/transfer/:city_id',
      name: 'TransfersRedirect',
      redirect: (to) => `/${lang}/transfer/${to.params.city_id}`,
    },
    {
      path: '/full-help',
      name: 'FullHelpRedirect',
      redirect: () => `/${lang}/full-help`,
    },
    {
      path: '/terms-and-conditions',
      name: 'TermsAndConditionsRedirect',
      redirect: () => `/${lang}/terms-and-conditions`,
    },

    {
      path: '/COR',
      name: 'AeroCor',
      redirect: () => {
        const { startDate, endDate } = getAeroCorSearchDates();

        return {
          path: `/${lang}/search`,
          query: {
            lat: '-31.3155152',
            lng: '-64.2136596',
            address: 'Aeropuerto Internacional de Córdoba',
            open_first: 'true',
            start_date: startDate,
            end_date: endDate,
          },
        };
      },
    },
    {
      path: '/lockers/:location',
      redirect: (to) => `/${lang}/lockers/${to.params.location}`,
    },

    // 🔚 Catch-all
    { path: '/404', name: 'NotFound', component: NotFound },
    {
      path: '*',
      redirect: () => '/404',
    },

  ],
});

// This callback runs before every route change, including on page load.
router.beforeEach((to, from, next) => {
  const supportedLangs = ['es', 'en', 'pt', 'it'];
  const pathSegments = to.path.split('/').filter(Boolean);
  const urlLang = pathSegments[0];
  const isTranslatedCityRoute = to.name && to.name.startsWith('CityHome');
  const isTranslatedLockerRoute = to.name && to.name.startsWith('LockerHome');

  // 1. Setear idioma
  if (supportedLangs.includes(urlLang)) {
    store.dispatch('setLanguage', urlLang);
    i18n.locale = urlLang;
    document.documentElement.setAttribute('lang', urlLang);
  }
  if (to.name === 'NotFound') {
    store.dispatch('setLanguage', detectLang());
    i18n.locale = detectLang();
    document.documentElement.setAttribute('lang', detectLang());
  }

  // 2. Validar city_id
  if (isTranslatedCityRoute && !cities.some((c) => c.id === to.params.city_id)) {
    return next('/404');
  }

  // 3. Validar location
  if (isTranslatedLockerRoute && !lockerLocations.some((l) => l.id === to.params.location)) {
    return next('/404');
  }

  // 4. Meta Tags
  if (to.name) {
    setHeadAttributes(urlLang, to.name, to.path);
  }

  return next();
});
export default router;
